<template>
  <div class="card" data-testid="choose-tags-overlay">
    <div class="p-8">
      <img class="block w-40 mx-auto" src="@/assets/omind_logo.png" alt="txt">
      <h1 class="mb-8 text-center" data-testid="choose-tags-title">
        {{ $t('TagSelectionOverlayComponent.chooseTags') }}</h1>
      <Form @submit="saveTags">
        <div v-for="tagQuestion in getTagQuestions" id="tmpl_tagQuestion" :key="tagQuestion"
             class="flex flex-col">
          <p class="text-center font-bold my-2">{{
              tagQuestion.hasOwnProperty('text') ? tagQuestion?.text[lang] : '-'
            }}</p>
          <div class="flex flex-wrap items-center justify-center" id="tmpl_tag_radio_container">
            <label class="w-fit m-1 relative flex cursor-pointer"
                id="tmpl_tag_radio" v-for="tag in getQuestionTags(tagQuestion.tags)" :key="tag">
                <!-- eslint-disable-next-line max-len -->
              <div class="flex p-4 w-full border border-[var(--color-light-grey)] rounded-lg my-0 mx-3" :class="{'text-[var(--color-orange)] bg-[var(--color-bisque)] border border-[var(--color-orange] font-semibold'
                   :isSelectedAnswer(tagQuestion._id, tag._id)}"
                   @click.prevent="selectTagAnswer(tagQuestion, tag._id)">
                <span>{{ tag.hasOwnProperty('tag') ? tag.tag[lang] : '-' }}</span>
              </div>
            </label>
          </div>
        </div>
        <div v-for="tagGroup in getUnprotectedGroups" :key="tagGroup._id"
             class="flex flex-col mt-8">
          <p class="text-center font-bold my-2">{{
              tagGroup.hasOwnProperty('text') ? tagGroup?.text[lang] : '-'
            }}</p>
          <div class="flex flex-wrap items-center justify-center"
              id="tmpl_tag_checkbox_container">
            <label class="block relative m-1 w-fit cursor-pointer"
              id="tmpl_tag_checkbox" v-for="tag in tagGroup.tags"
                   :key="tag._id" @click.prevent="toggleSelection(tag._id)">
                   <!-- eslint-disable-next-line max-len -->
              <div class="flex p-4 w-full border border-[var(--color-light-grey)] rounded-lg my-0 mx-3" :class="{'text-[var(--color-orange)] bg-[var(--color-bisque)] border border-[var(--color-orange] font-semibold': isChecked(tag)}">
                <span>{{ tag.hasOwnProperty('tag') ? tag.tag[lang] : '-' }}</span>
              </div>
            </label>
          </div>
        </div>
        <div class="flex justify-center">
          <button type="submit" name="regist" id="btn_regist" :disabled="isSubmitting"
          data-testid="choose-tags-submit"
          class="btn-primary-accent py-2.5 px-5 text-base sm:text-lg mx-auto mt-12">
            {{ $t('TagSelectionOverlayComponent.submit') }}
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import { Form } from '@primevue/forms';

export default {
  name: 'TagSelectionOverlayComponent',
  components: { Form },
  props: {
    overlay: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isSubmitting: false,
    };
  },
  computed: {
    // Get unprotected tag groups (checkbox/multi-select)
    // Map to old format for template compatibility (tagOptions -> tags, text -> tag)
    getUnprotectedGroups() {
      const tagGroups = this.$store.state.auth.tagGroups || [];
      return tagGroups.filter((g) => !g.protected).map((group) => ({
        ...group,
        // Map tagOptions to tags for template compatibility
        tags: (group.tagOptions || []).map((opt) => ({
          ...opt,
          tag: opt.text, // Map text to tag for template compatibility
        })),
      }));
    },
    // Get all unprotected tags flattened (for backward compatibility)
    getTags() {
      return this.getUnprotectedGroups.flatMap((group) => group.tags);
    },
    // Get protected tag groups (radio/single-select)
    // Map to old format for template compatibility (tagOptions -> tags)
    getTagQuestions() {
      const tagGroups = this.$store.state.auth.tagGroups || [];
      return tagGroups.filter((g) => g.protected).map((group) => ({
        ...group,
        // Map tagOptions to tags for template compatibility
        tags: (group.tagOptions || []).map((opt) => ({
          ...opt,
          tag: opt.text, // Map text to tag for template compatibility
        })),
      }));
    },
    lang() {
      return this.$i18n.locale;
    },
  },
  methods: {
    toggleSelection(id) {
      this.$store.commit('toggleTagSelection', id);
    },
    isChecked(tag) {
      return this.$store.state.auth.selectedTags.includes(tag._id);
    },
    // Get tag options for a protected group
    // The tags array is already mapped with 'tag' property from getTagQuestions
    getQuestionTags(tags) {
      return tags || [];
    },
    selectTagAnswer(tagGroup, tagOptionId) {
      this.$store.commit('setTagGroupAnswer', {
        groupId: tagGroup._id,
        tagOptionId,
      });
    },
    isSelectedAnswer(groupId, tagOptionId) {
      const answers = this.$store.state.auth.tagGroupAnswers || {};
      if (groupId in answers) {
        return tagOptionId === answers[groupId];
      }
      return false;
    },
    async saveTags() {
      try {
        this.isSubmitting = true;
        if (this.checkTagErrors()) {
          this.isSubmitting = false;
          return;
        }
        // Collect selected tag option IDs from protected groups (radio answers)
        const groupAnswers = this.$store.state.auth.tagGroupAnswers || {};
        const protectedTagIds = Object.values(groupAnswers);
        // Combine with unprotected selections (checkbox)
        const selectedTags = [
          ...this.$store.state.auth.selectedTags,
          ...protectedTagIds,
        ];
        await this.$store.dispatch('setTags', {
          id: this.overlay._id,
          tags: selectedTags,
        });
        this.isSubmitting = false;
      } catch (error) {
        this.isSubmitting = false;
        this.$toast.add({
          severity: 'error',
          summary: error.response?.data?.message || 'An error occurred',
          detail: '',
          life: 3000,
        });
      }
    },
    checkTagErrors() {
      let hasError = false;
      const protectedGroups = this.getTagQuestions;
      const answers = this.$store.state.auth.tagGroupAnswers || {};
      protectedGroups.forEach((group) => {
        if (!(group._id in answers)) {
          hasError = true;
          this.$toast.add({
            severity: 'error',
            summary: this.$t('TagSelectionOverlayComponent.pleaseAnswerQuestion', { question: group.text?.[this.lang] || '' }),
            detail: '',
            life: 3000,
          });
        }
      });
      return hasError;
    },
  },
  mounted() {
    // Fetch tag groups using new unified endpoint
    this.$store.dispatch('getTagGroups');
  },
  unmounted() {
    this.$store.commit('setSelectedTags', []);
    this.$store.commit('clearTagGroups');
    this.$store.commit('removeTagGroupAnswers');
  },
};
</script>
